// FIXED: 权限工具 - 定义模块、操作、默认权限模板

// 业务模块定义
export const MODULES = {
  PRODUCTS: 'products',
  INFLUENCERS: 'influencers',
  EXPRESS: 'express',
  SAMPLES: 'samples',
  SETTINGS: 'settings',
  // FIXED: 样品进度模块 - 独立权限管控
  SAMPLE_PROGRESS: 'sample_progress',
  // 发样审批模块
  APPROVAL: 'approval',
  // 打单模块
  PRINTING: 'printing',
  // 第三期：样品回收模块
  RETURNS: 'returns',
  // 第三期：风险预警模块
  RISK: 'risk',
} as const;

// 模块中文名映射
export const MODULE_LABELS: Record<string, string> = {
  products: '商品信息',
  influencers: '达人信息',
  express: '快递信息',
  samples: '发样信息',
  settings: '系统设置',
  // FIXED: 样品进度模块
  sample_progress: '样品进度',
  approval: '发样审批',
  printing: '打单管理',
  returns: '样品回收',
  risk: '风险预警',
};

// 操作类型定义
export const ACTIONS = {
  VIEW: 'view',
  ADD: 'add',
  EDIT: 'edit',
  DELETE: 'delete',
  EXPORT: 'export',
  SEND_SMS: 'sendSms'
} as const;

// 每个模块支持的操作
export const MODULE_ACTIONS: Record<string, string[]> = {
  products: ['view', 'add', 'edit', 'delete', 'export'],
  influencers: ['view', 'add', 'edit', 'delete', 'export'],
  express: ['view', 'add', 'edit', 'delete', 'export', 'sendSms'],
  samples: ['view', 'add', 'edit', 'delete', 'export'],
  // FIXED: 样品进度模块 - 支持查看/编辑(跟进)/删除/导出
  sample_progress: ['view', 'edit', 'delete', 'export'],
  // 发样审批模块 - 查看/审核(approve)/驳回(reject)
  approval: ['view', 'approve', 'reject'],
  // 打单模块 - 查看/打印(print)/录入单号(ship)
  printing: ['view', 'print', 'ship'],
  // 第三期：样品回收 - 查看/编辑(录入回收信息)
  returns: ['view', 'edit'],
  // 第三期：风险预警 - 仅查看
  risk: ['view'],
  settings: ['view']
};

// 操作中文名映射
export const ACTION_LABELS: Record<string, string> = {
  view: '查看',
  add: '新增',
  edit: '编辑',
  delete: '删除',
  export: '导出',
  sendSms: '发短信',
  approve: '审核通过',
  reject: '审核驳回',
  print: '打印面单',
  ship: '录入单号'
};

// 角色定义
export type Role = 'admin' | 'leader' | 'staff' | 'printer';

export const ROLE_LABELS: Record<Role, string> = {
  admin: '管理员',
  leader: '组长',
  staff: '员工',
  printer: '打单员'
};

// 工种选项（固定3种）
export const JOB_TYPES = ['商务员', '发货员', '打单员'] as const;

// 权限配置接口
export interface ModulePermission {
  enabled: boolean;
  actions: string[];
}

export interface PermissionConfig {
  modules: Record<string, ModulePermission>;
}

// 默认权限模板（新建员工时的初始权限：仅查看）
export function getDefaultPermission(role: Role): PermissionConfig {
  if (role === 'admin') {
    // 管理员：全部模块启用，全部操作
    return {
      modules: {
        products: { enabled: true, actions: [...MODULE_ACTIONS.products] },
        influencers: { enabled: true, actions: [...MODULE_ACTIONS.influencers] },
        express: { enabled: true, actions: [...MODULE_ACTIONS.express] },
        samples: { enabled: true, actions: [...MODULE_ACTIONS.samples] },
        sample_progress: { enabled: true, actions: [...MODULE_ACTIONS.sample_progress] },
        approval: { enabled: true, actions: [...MODULE_ACTIONS.approval] },
        printing: { enabled: true, actions: [...MODULE_ACTIONS.printing] },
        returns: { enabled: true, actions: [...MODULE_ACTIONS.returns] },
        risk: { enabled: true, actions: [...MODULE_ACTIONS.risk] },
        settings: { enabled: true, actions: ['view'] }
      }
    };
  }

  if (role === 'leader') {
    // 组长：可查看+审批，无系统设置
    return {
      modules: {
        products: { enabled: true, actions: ['view'] },
        influencers: { enabled: true, actions: ['view'] },
        express: { enabled: true, actions: ['view'] },
        samples: { enabled: true, actions: ['view'] },
        sample_progress: { enabled: true, actions: ['view', 'edit'] },
        approval: { enabled: true, actions: ['view', 'approve', 'reject'] },
        printing: { enabled: false, actions: [] },
        returns: { enabled: true, actions: ['view', 'edit'] },
        risk: { enabled: true, actions: ['view'] },
        settings: { enabled: false, actions: [] }
      }
    };
  }

  if (role === 'printer') {
    // 打单员：仅打单模块
    return {
      modules: {
        products: { enabled: false, actions: [] },
        influencers: { enabled: false, actions: [] },
        express: { enabled: true, actions: ['view'] },
        samples: { enabled: false, actions: [] },
        sample_progress: { enabled: false, actions: [] },
        approval: { enabled: false, actions: [] },
        printing: { enabled: true, actions: ['view', 'print', 'ship'] },
        returns: { enabled: false, actions: [] },
        risk: { enabled: false, actions: [] },
        settings: { enabled: false, actions: [] }
      }
    };
  }

  // staff：默认只有查看权限，无系统设置
  return {
    modules: {
      products: { enabled: true, actions: ['view'] },
      influencers: { enabled: true, actions: ['view'] },
      express: { enabled: true, actions: ['view'] },
      samples: { enabled: true, actions: ['view'] },
      sample_progress: { enabled: true, actions: ['view', 'edit'] },
      approval: { enabled: false, actions: [] },
      printing: { enabled: false, actions: [] },
      returns: { enabled: true, actions: ['view', 'edit'] },
      risk: { enabled: true, actions: ['view'] },
      settings: { enabled: false, actions: [] }
    }
  };
}

// 强制规则：staff/leader 的系统设置权限永远为 false
export function enforcePermissionRules(role: Role, permissions: PermissionConfig): PermissionConfig {
  if (role === 'admin' || role === 'merchant') {
    // admin 或 merchant 拥有全部权限
    return getDefaultPermission('admin');
  }

  // printer：仅打单模块，其他全部关闭
  if (role === 'printer') {
    return getDefaultPermission('printer');
  }

  // staff/leader：强制关闭系统设置 + 样品进度不可删除
  const result: PermissionConfig = JSON.parse(JSON.stringify(permissions));
  if (result.modules && result.modules.settings) {
    result.modules.settings.enabled = false;
    result.modules.settings.actions = [];
  } else if (result.modules) {
    result.modules.settings = { enabled: false, actions: [] };
  }
  // 非管理员样品进度不可删除
  if (result.modules && result.modules.sample_progress) {
    result.modules.sample_progress.actions = result.modules.sample_progress.actions.filter((a: string) => a !== 'delete');
  } else if (result.modules) {
    result.modules.sample_progress = { enabled: true, actions: ['view', 'edit'] };
  }
  // 非管理员默认无导出权限（需管理员单独授权）
  if (result.modules) {
    for (const key of Object.keys(result.modules)) {
      if (key === 'settings') continue;
      const mod = result.modules[key];
      if (mod && mod.actions) {
        mod.actions = mod.actions.filter((a: string) => a !== 'export');
      }
    }
  }
  return result;
}

// 检查权限
export function checkPermission(
  permissions: PermissionConfig | null,
  role: Role,
  module: string,
  action: string
): boolean {
  // admin 或 merchant 拥有全部权限（merchant=本客户端管理员）
  if (role === 'admin' || role === 'merchant') return true;

  // 无权限配置
  if (!permissions || !permissions.modules) return false;

  const modulePerm = permissions.modules[module];
  if (!modulePerm || !modulePerm.enabled) return false;

  // 'view' 操作：只要模块启用就能查看
  if (action === 'view') return true;

  return modulePerm.actions.includes(action);
}

// 检查菜单是否可见
export function checkMenuVisible(
  permissions: PermissionConfig | null,
  role: Role,
  module: string
): boolean {
  // admin 或 merchant 拥有全部权限
  if (role === 'admin' || role === 'merchant') return true;
  if (!permissions || !permissions.modules) return false;
  const modulePerm = permissions.modules[module];
  return !!(modulePerm && modulePerm.enabled);
}
